import { Box, Flex, Heading, Icon, Text, VStack, useToast } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { GoogleReCaptchaProvider } from '@google-recaptcha/react';
import CustomButton from 'components/CustomButton';
import InputNumber from 'components/InputNumber';
import PagSeguroCardPrivate from 'components/sidebar/components/PagSeguroCardPrivate';
import useAuth from 'contexts/useAuth';
import React from 'react';
import { useForm } from 'react-hook-form';
import { FaCreditCard } from 'react-icons/fa';
import { FiCopy } from 'react-icons/fi';
import { IoCheckmarkCircle } from 'react-icons/io5';
import { QRCode } from 'react-qrcode-logo';
import { useCopyToClipboard } from 'react-use';
import { generateInstantCharge, getCharge } from 'services/api.service';
import { InstantQrCode } from 'types/Charge';
import { maskCurrency } from 'utils/number';
import { getVariable } from 'whitelabel';

// Cartão exige valor mínimo (regra herdada do antigo "Carregar Saldo"). Pix aceita qualquer valor > 0.
const CARD_MIN_AMOUNT = 100;

const DepositPage: React.FC = () => {
  const toast = useToast();
  const { userData } = useAuth();
  const [, copyToClipboard] = useCopyToClipboard();
  const { control, handleSubmit } = useForm<{ amount: number }>();

  const features = userData?.features as
    | { deposit?: boolean; allowCardRecharge?: boolean }
    | undefined;
  const depositEnabled = features?.deposit;
  const cardEnabled = features?.allowCardRecharge === true;
  const pageEnabled = depositEnabled || cardEnabled;

  const [loading, setLoading] = React.useState(false);
  const [charge, setCharge] = React.useState<InstantQrCode | null>(null);
  const [cardAmount, setCardAmount] = React.useState(0);

  // Enquanto o QR está na tela, faz polling do status a cada segundo.
  const { data: polled } = useQuery(['deposit-charge', charge?.id], () => getCharge(charge!.id), {
    enabled: !!charge,
    refetchInterval: (data) => {
      const status = (data as { status?: string } | undefined)?.status;
      if (status && status !== 'PENDING') return false;
      return 1000;
    },
  });

  const status = (polled as { status?: string } | undefined)?.status ?? 'PENDING';
  const isPaid = status === 'PAID' || status === 'FINISHED';

  // Pix: gera o QR Code de depósito e inicia o polling.
  const onSubmitPix = async (form: { amount: number }) => {
    if (!form.amount || form.amount <= 0) return;
    setLoading(true);
    try {
      const created = await generateInstantCharge({ amount: form.amount, deposit: true });
      setCharge(created);
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Erro ao gerar QR Code de depósito.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Cartão: valida o mínimo e abre o modal de pagamento com o valor digitado.
  const onSubmitCard = (form: { amount: number }) => {
    if (!form.amount || form.amount < CARD_MIN_AMOUNT) {
      toast({
        title: 'Valor mínimo de R$ 100,00 para cartão',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setCardAmount(form.amount);
  };

  const reset = () => setCharge(null);

  const copyCode = () => {
    if (!charge) return;
    copyToClipboard(charge.qrCode);
    toast({
      title: 'Código copiado',
      position: 'top',
      status: 'success',
      duration: 1500,
      isClosable: true,
    });
  };

  if (!pageEnabled) {
    return (
      <Flex direction="column" pt={{ base: '120px', md: '80px' }} align="center">
        <Box bg="white" borderRadius="20px" p={8} w={{ base: '100%', md: '480px' }} boxShadow="sm">
          <VStack spacing={4} align="stretch">
            <Heading size="lg">Depositar</Heading>
            <Text color="gray.500">
              Depósito não habilitado para esta conta. Fale com o suporte para liberar.
            </Text>
          </VStack>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex direction="column" pt={{ base: '120px', md: '80px' }} align="center">
      <Box bg="white" borderRadius="20px" p={8} w={{ base: '100%', md: '480px' }} boxShadow="sm">
        <VStack spacing={6} align="stretch">
          <Heading size="lg">Depositar</Heading>

          {!charge && (
            <>
              <Text color="gray.500">
                Informe o valor e escolha como depositar. O valor é creditado no seu saldo, sem
                gerar venda.
              </Text>
              <InputNumber
                control={control}
                name="amount"
                label="Valor do depósito"
                placeholder="R$ 0,00"
                rules={{
                  required: 'Informe o valor',
                  validate: {
                    positive: (v: number) => v > 0 || 'O valor deve ser maior que 0',
                  },
                }}
              />
              <VStack spacing={3} align="stretch">
                {depositEnabled && (
                  <CustomButton
                    colorScheme="green"
                    isLoading={loading}
                    onClick={handleSubmit(onSubmitPix)}
                  >
                    QR Code Pix
                  </CustomButton>
                )}
                {cardEnabled && (
                  <CustomButton
                    colorScheme="brand"
                    variant="solid"
                    leftIcon={<FaCreditCard size={20} />}
                    onClick={handleSubmit(onSubmitCard)}
                  >
                    Cartão de Crédito
                  </CustomButton>
                )}
              </VStack>
            </>
          )}

          {charge && !isPaid && (
            <VStack spacing={5} align="center">
              <Text>
                <strong>Valor:</strong> {maskCurrency(Number(charge.amount))}
              </Text>
              {charge.fee != null && charge.netAmount != null && (
                <Text color="gray.500" fontSize="sm">
                  Taxa {maskCurrency(charge.fee)} · Você receberá {maskCurrency(charge.netAmount)}
                </Text>
              )}
              <QRCode
                value={charge.qrCode}
                logoImage={getVariable('logo')}
                size={256}
                removeQrCodeBehindLogo
              />
              <CustomButton leftIcon={<FiCopy />} onClick={copyCode}>
                Copiar código Pix
              </CustomButton>
              <Text color="gray.400" fontSize="sm">
                Aguardando pagamento...
              </Text>
              <CustomButton variant="ghost" onClick={reset}>
                Novo depósito
              </CustomButton>
            </VStack>
          )}

          {charge && isPaid && (
            <VStack spacing={5} align="center">
              <Icon as={IoCheckmarkCircle} boxSize={20} color="green.400" />
              <Heading size="md">Depósito recebido</Heading>
              <Text>
                <strong>Valor:</strong> {maskCurrency(Number(charge.amount))}
              </Text>
              {charge.netAmount != null && (
                <Text color="gray.500" fontSize="sm">
                  Creditado {maskCurrency(charge.netAmount)}
                  {charge.fee ? ` (taxa ${maskCurrency(charge.fee)})` : ''}
                </Text>
              )}
              <CustomButton colorScheme="green" onClick={reset}>
                Novo depósito
              </CustomButton>
            </VStack>
          )}
        </VStack>
      </Box>

      {cardAmount > 0 && (
        <GoogleReCaptchaProvider
          siteKey={process.env.REACT_APP_RECAPTCHA}
          type="v2-invisible"
          scriptProps={{
            async: false,
            defer: false,
            appendTo: 'head',
            nonce: undefined,
          }}
        >
          <PagSeguroCardPrivate
            method="CREDIT_CARD"
            amount={cardAmount}
            isOpen={cardAmount > 0}
            onClose={() => setCardAmount(0)}
          />
        </GoogleReCaptchaProvider>
      )}
    </Flex>
  );
};

export default DepositPage;
